import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getMalnutritionRiskAssessment,
  getPatientWeights,
  postMalnutritionFeedback,
} from "./endpoints";
import { malnutritionUseMocks } from "./mocks";

import type { UseMutationResult, UseQueryResult } from "react-query";
import type {
  MalnutritionRiskAssessment,
  PatientWeightHistory,
} from "../../types/Malnutrition";

// Recent weights update as new readings are charted, so poll while the embed is
// open (foreground only — refetchIntervalInBackground stays off globally).
const WEIGHTS_REFETCH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export const useMalnutritionRiskQuery = (
  emrPatientID: string,
  emrHospitalID: string
): UseQueryResult<MalnutritionRiskAssessment, Error> => {
  // The fetcher returns mock vs real data based on the ?mock= param, so the mode
  // is part of the cache identity — otherwise toggling ?mock= without a full
  // reload could serve cached mock data in real mode (or vice versa).
  const useMocks = malnutritionUseMocks();
  return useQuery<MalnutritionRiskAssessment, Error>(
    ["malnutritionrisk", emrPatientID, emrHospitalID, useMocks],
    () => getMalnutritionRiskAssessment(emrPatientID, emrHospitalID),
    { enabled: !!emrPatientID && !!emrHospitalID }
  );
};

export const usePatientWeightsQuery = (
  emrPatientID: string,
  emrHospitalID: string
): UseQueryResult<PatientWeightHistory, Error> => {
  const useMocks = malnutritionUseMocks();
  return useQuery<PatientWeightHistory, Error>(
    ["patientweights", emrPatientID, emrHospitalID, useMocks],
    () => getPatientWeights(emrPatientID, emrHospitalID),
    {
      enabled: !!emrPatientID && !!emrHospitalID,
      refetchInterval: WEIGHTS_REFETCH_INTERVAL_MS,
    }
  );
};

// Records the user's "good candidate?" answer (variable is is_good_candidate,
// true = Yes). On success we invalidate the risk-assessment query so the card
// re-reads the stored answer; the key prefix match ignores the trailing
// useMocks segment.
export const useMalnutritionFeedbackMutation = (
  emrPatientID: string,
  emrHospitalID: string
): UseMutationResult<void, Error, boolean> => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, boolean>(
    (isGoodCandidate: boolean) =>
      postMalnutritionFeedback(emrPatientID, emrHospitalID, isGoodCandidate),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "malnutritionrisk",
          emrPatientID,
          emrHospitalID,
        ]);
      },
    }
  );
};
